import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

export interface CartItem {
    produtoId: number;
    variacaoId: number | null;
    cor: string | null;
    nome: string;
    sku: string;
    preco: number;
    tamanho: string;
    quantidade: number;
    imagem: string | null;
}

export type CartRef = Pick<CartItem, 'produtoId' | 'variacaoId' | 'tamanho'>;

interface CartState {
    itens: CartItem[];
}

// v2: item passou a carregar cor/variação — carrinho v1 antigo é descartado.
const STORAGE_KEY = 'loja.cart.v2';
const MAX_QTD = 99;

const carregar = (): CartItem[] => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const persistir = (itens: CartItem[]) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(itens));
    } catch {
        // Modo privativo / quota estourada: o carrinho segue só em memória.
    }
};

const initialState: CartState = { itens: carregar() };

const chave = (item: Pick<CartItem, 'produtoId' | 'variacaoId' | 'tamanho'>) => `${item.produtoId}::${item.variacaoId ?? '-'}::${item.tamanho}`;

const lojaCartSlice = createSlice({
    name: 'lojaCart',
    initialState,
    reducers: {
        addCartItem(state, action: PayloadAction<CartItem>) {
            const novo = action.payload;
            const existente = state.itens.find((i) => chave(i) === chave(novo));

            if (existente) {
                existente.quantidade = Math.min(MAX_QTD, existente.quantidade + novo.quantidade);
                // Preço/nome podem ter mudado no painel desde que o item entrou no carrinho.
                existente.preco = novo.preco;
                existente.nome = novo.nome;
                existente.cor = novo.cor;
            } else {
                state.itens.push({ ...novo, quantidade: Math.min(MAX_QTD, novo.quantidade) });
            }

            persistir(state.itens);
        },
        setCartItemQtd(state, action: PayloadAction<CartRef & { quantidade: number }>) {
            const { quantidade, ...ref } = action.payload;

            if (quantidade <= 0) {
                state.itens = state.itens.filter((i) => chave(i) !== chave(ref));
            } else {
                const item = state.itens.find((i) => chave(i) === chave(ref));
                if (item) item.quantidade = Math.min(MAX_QTD, quantidade);
            }

            persistir(state.itens);
        },
        removeCartItem(state, action: PayloadAction<CartRef>) {
            state.itens = state.itens.filter((i) => chave(i) !== chave(action.payload));
            persistir(state.itens);
        },
        clearCart(state) {
            state.itens = [];
            persistir(state.itens);
        },
    },
});

export const { addCartItem, setCartItemQtd, removeCartItem, clearCart } = lojaCartSlice.actions;

export default lojaCartSlice.reducer;
